import { useState, useMemo, useCallback } from 'react';
import { Upload, ImageIcon, Search, X } from 'lucide-react';
import { useAssetsPaginated } from '../../hooks/useAssetsPaginated';
import { useAssetCategories } from '../../hooks/useAssetCategories';
import { useImageUpload } from '../../hooks/useImageUpload';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { assetsApi } from '../../services/assetsApi';
import { runValidation } from '../../lib/validation';
import type { ValidationResult } from '../../lib/validation';
import type { Asset, AssetUsage } from '../../services/assetsApi';
import {
  AssetGrid, AssetPreviewOverlay, UploadValidationBanner, UploadZone,
  PageHeader, PageContent, TextField, TextFieldSlot, IconButton,
  EmptyState, LoadingState, AlertDialog, Tabs,
} from '@printtat/ui';

/** Map UI filter tab key to API query params */
const parseFilter = (activeFilter: string): { source?: 'user' | 'printtat'; category?: string } => {
  if (activeFilter === 'user') return { source: 'user' };
  if (activeFilter === 'printtat') return { source: 'printtat' };
  if (activeFilter === 'printtat:uncategorized') return { source: 'printtat', category: 'uncategorized' };
  if (activeFilter.startsWith('cat:')) return { source: 'printtat', category: activeFilter.slice(4) };
  return {};
};

export const AssetsPage = () => {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const [activeFilter, setActiveFilter] = useState('all');

  const { source, category } = parseFilter(activeFilter);
  const search = debouncedSearch || undefined;

  const {
    assets, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
    addAsset, removeAsset, updateAsset,
  } = useAssetsPaginated({ source, category, search });

  const { pending, errors, warnings, uploadFile, dismissErrors, dismissWarnings } =
    useImageUpload({ onUploadSuccess: addAsset });

  const { categories } = useAssetCategories();

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [previewValidation, setPreviewValidation] = useState<ValidationResult[]>([]);
  const [usageWarning, setUsageWarning] = useState<{ assetId: string; usedBy: AssetUsage[] } | null>(null);

  const filterTabs = useMemo(() => [
    { key: 'all', label: 'All' },
    { key: 'user', label: 'My Uploads' },
    ...categories.map(c => ({ key: `cat:${c.slug}`, label: c.name })),
    ...(categories.length > 0 ? [{ key: 'printtat:uncategorized', label: 'Uncategorized' }] : [{ key: 'printtat', label: 'Printtat' }]),
  ], [categories]);

  const handleAssetClick = async (asset: Asset) => {
    setSelectedAsset(asset);
    try {
      const img = new Image();
      // crossOrigin before src so the request goes out with CORS headers and
      // doesn't poison the cache for later Fabric-canvas loads of the same URL.
      img.crossOrigin = 'anonymous';
      img.src = asset.url;
      await new Promise<void>((resolve) => { img.onload = () => resolve(); img.onerror = () => resolve(); });
      const results = await runValidation('upload', {
        file: new File([], asset.originalName, { type: asset.mimeType }),
        imageElement: img,
      });
      setPreviewValidation(results);
    } catch {
      setPreviewValidation([]);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const result = await assetsApi.delete(id);
      if (result.conflict && result.usedBy?.length) {
        setUsageWarning({ assetId: id, usedBy: result.usedBy });
        return;
      }
      removeAsset(id);
      if (selectedAsset?.id === id) setSelectedAsset(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleForceDelete = async () => {
    if (!usageWarning) return;
    const { assetId } = usageWarning;
    setUsageWarning(null);
    try {
      await assetsApi.delete(assetId, true);
      removeAsset(assetId);
      if (selectedAsset?.id === assetId) setSelectedAsset(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleRename = async (id: string, newName: string) => {
    try {
      const updated = await assetsApi.rename(id, newName);
      updateAsset(id, { name: updated.name });
      if (selectedAsset?.id === id) setSelectedAsset(prev => prev ? { ...prev, name: updated.name } : null);
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleBulkUpload = (files: File[]) => {
    files.forEach(f => uploadFile(f));
  };

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const assetItems = assets.map(a => ({
    id: a.id,
    name: a.name,
    src: a.url,
    size: a.size,
    width: a.width,
    height: a.height,
    mimeType: a.mimeType,
    hasWarning: a.hasTransparency === false,
    warningText: a.hasTransparency === false ? 'No transparent background detected. The image may cover the garment behind it.' : undefined,
    source: a.source,
  }));

  return (
    <div className="flex h-full">
      {/* Main content */}
      <PageContent className="flex-1 overflow-y-auto">
        <PageHeader
          icon={ImageIcon}
          title="Library"
          description="Manage your uploaded assets and images"
          actions={
            <label className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer">
              <Upload size={16} className="inline-block" />
              Upload
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    handleBulkUpload(Array.from(e.target.files));
                    e.target.value = '';
                  }
                }}
              />
            </label>
          }
        />

        {/* Upload zone */}
        <UploadZone onFiles={handleBulkUpload} multiple />

        {/* Validation banners */}
        <div className="mt-4">
          <UploadValidationBanner
            errors={errors}
            warnings={warnings}
            onDismissErrors={dismissErrors}
            onDismissWarnings={dismissWarnings}
          />
        </div>

        {/* Search */}
        <div className="mt-4 max-w-sm">
          <TextField
            placeholder="Search assets..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          >
            <TextFieldSlot>
              <Search className="w-4 h-4 text-gray-400" />
            </TextFieldSlot>
            {searchInput && (
              <TextFieldSlot>
                <IconButton size="xs" onClick={() => setSearchInput('')}>
                  <X className="w-3.5 h-3.5" />
                </IconButton>
              </TextFieldSlot>
            )}
          </TextField>
        </div>

        {/* Filter tabs */}
        <div className="mt-4">
          <Tabs
            tabs={filterTabs}
            value={activeFilter}
            onChange={setActiveFilter}
            variant="pill"
          />
        </div>

        {/* Unified asset grid */}
        <div className="mt-4">
          {isLoading ? (
            <LoadingState variant="skeleton" columns={4} />
          ) : assets.length === 0 && pending.length === 0 ? (
            <EmptyState
              icon={Upload}
              title={
                search ? 'No matching assets' :
                activeFilter === 'user' ? 'No uploads yet' :
                activeFilter.startsWith('cat:') || activeFilter.startsWith('printtat') ? 'No assets in this category' :
                'No assets yet'
              }
              description={
                search ? 'Try a different search term' :
                activeFilter === 'user' || activeFilter === 'all' ? 'Upload images above to get started' : undefined
              }
            />
          ) : (
            <AssetGrid
              assets={assetItems}
              columns={4}
              showControls={(item) => item.source === 'user'}
              pendingUploads={activeFilter === 'all' || activeFilter === 'user' ? pending : []}
              onAssetClick={(item) => {
                const asset = assets.find(a => a.id === item.id);
                if (asset) handleAssetClick(asset);
              }}
              onDelete={handleDelete}
              onRename={handleRename}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>
      </PageContent>

      {/* Preview overlay */}
      {selectedAsset && (
        <AssetPreviewOverlay
          asset={selectedAsset}
          validationResults={previewValidation}
          readOnly={selectedAsset.source === 'printtat'}
          onClose={() => setSelectedAsset(null)}
          onRename={handleRename}
          onDelete={handleDelete}
        />
      )}

      {/* Usage warning dialog */}
      <AlertDialog
        open={!!usageWarning}
        onOpenChange={(open) => { if (!open) setUsageWarning(null); }}
        title="Image is used in designs"
        description={
          usageWarning
            ? `This image is used in ${usageWarning.usedBy.length === 1 ? '1 design' : `${usageWarning.usedBy.length} designs`}: ${usageWarning.usedBy.map(d => d.name).join(', ')}. Deleting it will leave broken images in those designs.`
            : ''
        }
        confirmText="Delete anyway"
        variant="destructive"
        onConfirm={handleForceDelete}
        onCancel={() => setUsageWarning(null)}
      />
    </div>
  );
};
